import React, { useEffect, useState } from 'react';
import "./home.css";
import "react-simple-flex-grid/lib/main.css";
import { GeoLocationService } from "../services/GeoLocationService";
import { MasterDataService } from "../services/MasterDataService";
import { Button, Box, Typography, Link } from "@mui/material"
import { useNavigate, useLocation } from "react-router-dom";
import UseWindowDimensions from "../components/GetWindowDimensions";
import { styled } from "@mui/system";
import Search from "../components/Search";
import queryString from 'query-string';
import { DefaultAvailability, DefaultDistance } from "../components/SearchBar";
import { Helmet } from "react-helmet";
import { MUI_CATEGORY_ICONS, LEGACY_CATEGORY_ICONS, DEFAULT_CATEGORY_CONFIGS } from "../components/categoryIconRegistry";
import { Constants } from "../Constants";
import { useIsProvider } from "../hooks/useIsProvider";

const buildCategoryButtons = (categories, configs) => {
    const keys = Array.isArray(categories) && categories.length > 0
        ? categories
        : DEFAULT_CATEGORY_CONFIGS.map((c) => c.key);
    const configByKey = {};
    (configs || []).forEach((c) => { if (c && c.key) configByKey[c.key] = c; });
    const defaultByKey = {};
    DEFAULT_CATEGORY_CONFIGS.forEach((c) => { defaultByKey[c.key] = c; });
    const entries = keys.map((key) => {
        const cfg = configByKey[key];
        const dflt = defaultByKey[key];
        return {
            key,
            displayName: (cfg && cfg.displayName) || (dflt && dflt.displayName) || key,
            icon: (cfg && cfg.icon) || (dflt && dflt.icon) || '',
            order: cfg ? (cfg.order || 0) : ((dflt && dflt.order) || 0),
            hidden: cfg ? Boolean(cfg.hidden) : false,
        };
    });
    const positioned = entries.filter((e) => !e.hidden && e.order > 0).sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
    const auto = entries.filter((e) => !e.hidden && e.order === 0).sort((a, b) => a.key.localeCompare(b.key));
    return [...positioned, ...auto];
};

const StyledButton = styled(Button, {
    name: "StyledButton",
})(() => ({
    marginLeft: "5px",
    marginRight: "5px",
    width: "64px",
    height: "85px",
    display: "inline",
    background: "white",
    padding: "0px",
    textTransform: "None"
})
);

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {width} = UseWindowDimensions();
    const isProvider = useIsProvider();

    const parsed = queryString.parse(location.search);
    if (parsed.latitude === "undefined") parsed.latitude = null;

    const [geolocation, setGeolocation] = useState([parsed.latitude, parsed.longitude, "", parsed.accuracy / 0.62137119]);
    const [Category, setCategory] = useState(parsed.category);
    const [Latitude, setLatitude] = useState(parsed.latitude);
    const [Longitude, setLongitude] = useState(parsed.longitude);
    const [Accuracy, setAccuracy] = useState(parsed.accuracy);
    const [categoryConfigs, setCategoryConfigs] = useState(DEFAULT_CATEGORY_CONFIGS);
    // City/state are derived directly from the URL on every render — NOT
    // mirrored into local state. The original PR #429 had `const [City, setCity]`
    // / `const [State, setState]` plus a catch-up useEffect that pulled from
    // parsed.city/parsed.state on URL changes. That created a staleness window
    // when clearing: SearchBar would navigate to a URL without city/state, the
    // URL-writeback useEffect below would run in the SAME render with stale
    // City/State still truthy, see `hasCityState=true`, and navigate back to
    // the city URL — silently undoing the user's clear. Single-sourcing from
    // `parsed.*` eliminates the window entirely.
    const City = parsed.city;
    const State = parsed.state;
    const [kidstoggle, setKidstoggle] = useState(parsed.kidstoggle === "true");
    const [Page, setPage] = useState(parsed.page ? parseInt(parsed.page) : 1);
    const [View, setView] = useState(parsed.view || 'list');

    const getGeoLocation = () => {
        let accuracy = geolocation[3] * 0.62137119;
        setLatitude(geolocation[0]);
        setLongitude(geolocation[1]);
        setAccuracy(accuracy);
    };

    useEffect(() => {
        const hasBounds = parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude;
        const hasGeoParams = parsed.latitude && parsed.longitude && parsed.distance;
        const hasCityState = parsed.city && parsed.state && !hasGeoParams;
        if (!hasBounds && !hasCityState && (!parsed.latitude || !parsed.longitude || !parsed.accuracy)) {
            GeoLocationService.getGeoLocation().then((response) => setGeolocation([response.latitude, response.longitude, response.city, response.accuracy]));
        }
    }, [parsed.accuracy, parsed.latitude, parsed.longitude, parsed.city, parsed.state, parsed.distance, parsed.minLatitude, parsed.maxLatitude, parsed.minLongitude, parsed.maxLongitude]);

    useEffect(() => {
        if (geolocation) getGeoLocation();
    }, [geolocation, getGeoLocation]);

    useEffect(() => {
        const urlPage = parsed.page ? parseInt(parsed.page) : 1;
        if (urlPage !== Page) {
            setPage(urlPage);
        }
        const urlView = parsed.view || 'list';
        if (urlView !== View) {
            setView(urlView);
        }
        // City/State are no longer mirrored into local state (see above);
        // they always read fresh from `parsed.*`. No catch-up needed here.
    }, [location.search]);

    useEffect(() => {
        Promise.all([MasterDataService.getAllCategories(), MasterDataService.getCategoryConfigs()])
            .then(([categories, configs]) => {
                const merged = buildCategoryButtons(categories, configs);
                if (merged.length > 0) setCategoryConfigs(merged);
            })
            .catch((e) => console.error('Failed to load categories', e));
    }, []);

    useEffect(() => {
        let hasToken = window.location.hash != false;
        const hasBounds = parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude;
        const hasGeoParams = parsed.latitude && parsed.longitude && parsed.distance;
        const hasCityState = City && State && !hasGeoParams && !hasBounds;
        if (hasBounds) {
            // Area/bounds mode: preserve bounds and other params
            if (!hasToken) navigate('?minLatitude=' + parsed.minLatitude + '&maxLatitude=' + parsed.maxLatitude + '&minLongitude=' + parsed.minLongitude + '&maxLongitude=' + parsed.maxLongitude + '&category=' + (Category || "") + '&availability=' + (parsed.availability || DefaultAvailability) + '&kidstoggle=' + kidstoggle + '&page=' + Page + '&view=' + View, { replace: true });
        }
        else if (hasCityState) {
            // City/state mode: ignore lat/lon
            if (!hasToken) navigate('?city=' + encodeURIComponent(City) + '&state=' + encodeURIComponent(State) + '&category=' + (Category || "") + '&availability=' + (parsed.availability || DefaultAvailability) + '&kidstoggle=' + kidstoggle + '&page=' + Page + '&view=' + View, { replace: true });
        }
        else if (parsed.latitude == null) {
            // Need to trigger geo lookup
            var d = parsed.distance;
            if (!d) d = DefaultDistance;
            var a = parsed.availability;
            if (!a) a = DefaultAvailability;
            var c = Category;
            if (!Category) {
                c = "Massage";
                setCategory(c);
            }
            if (!hasToken) navigate('?latitude=' + Latitude + '&longitude=' + Longitude + '&distance=' + d + '&accuracy=' + Accuracy + '&category=' + c + '&availability=' + a + '&kidstoggle=' + kidstoggle + '&page=' + Page + '&view=' + View, { replace: true });
        }
        else {
            // Geo mode with URL params already set: preserve the URL's own geo values
            // (single-source from parsed.*, same rationale as City/State above) so a
            // city-centered navigation isn't clobbered by stale geolocation state.
            const lat = parsed.latitude != null ? parsed.latitude : Latitude;
            const lon = parsed.longitude != null ? parsed.longitude : Longitude;
            const acc = parsed.accuracy != null ? parsed.accuracy : Accuracy;
            const cityParams = (City && State) ? '&city=' + encodeURIComponent(City) + '&state=' + encodeURIComponent(State) : '';
            if (!hasToken) navigate('?latitude=' + lat + '&longitude=' + lon + '&distance=' + parsed.distance + '&accuracy=' + acc + '&category=' + Category + '&availability=' + parsed.availability + cityParams + '&kidstoggle=' + kidstoggle + '&page=' + Page + '&view=' + View, { replace: true });
        }
    }, [Category, Latitude, Longitude, Accuracy, parsed.availability, parsed.distance, navigate, parsed.latitude, kidstoggle, Page, View, parsed.city, parsed.state, parsed.longitude, parsed.minLatitude, parsed.maxLatitude, parsed.minLongitude, parsed.maxLongitude])

    const handleQuickSearchButtonClick = (event) => {
        let cat = event.currentTarget.id
        setCategory(cat);
        setKidstoggle(false);
        setPage(1); // Reset to page 1 on new search
    }

    const handleKidsClick = () => {
        if (Category !== "Kids")
            setKidstoggle(!kidstoggle)
    };

    const geoMode = Boolean(parsed.latitude && parsed.longitude && parsed.distance);

    return (
        <Box>
            <Helmet>
                <title>PairCare - Home</title>
            </Helmet>
            <Box width={"calc(100vw - 20px)"} className="outerContainer" position={"sticky"} top={"64px"} style={{ background: "white" }} display={"flex"} zIndex={1} paddingRight={"20px"} paddingLeft={"20px"}>

                <Box flexWrap={"wrap"} flexDirection={"row"} display={"flex"} alignItems="center" margin={"auto"} sx={{
                    '@media (max-width:651px)': {
                        width: 300,
                    }
                }} >
                    {categoryConfigs.map((config) => {
                            const LegacyIcon = LEGACY_CATEGORY_ICONS[config.icon];
                            const MuiIcon = !LegacyIcon ? MUI_CATEGORY_ICONS[config.icon] : null;
                            const isSelected = Category === config.key;
                            return (
                                <StyledButton
                                    key={config.key}
                                    id={config.key}
                                    data-testid={config.key.toLowerCase()}
                                    onClick={handleQuickSearchButtonClick}
                                >
                                    {LegacyIcon && (
                                        <svg aria-labelledby={config.key} width={"30px"} height={"30"}>
                                            <LegacyIcon
                                                width={"30"}
                                                height={"30"}
                                                fill={isSelected ? "black" : "gray"}
                                                stroke={isSelected ? "black" : "gray"}
                                                strokeWidth={config.key === 'Massage' ? "0" : undefined}
                                            />
                                        </svg>
                                    )}
                                    {MuiIcon && (
                                        <MuiIcon sx={{ fontSize: 30, color: isSelected ? "black" : "gray" }} />
                                    )}
                                    <Typography
                                        fontSize={11}
                                        fontWeight={"bold"}
                                        color={isSelected ? "black" : "gray"}
                                        fontFamily={"arial"}
                                    >
                                        {config.displayName}
                                    </Typography>
                                    <Typography
                                        color={"black"}
                                        visibility={isSelected ? "visible" : "hidden"}
                                        marginTop={"-10px"}
                                    >
                                        _____
                                    </Typography>
                                </StyledButton>
                            );
                        })}

                    {/*<StyledButton id={"Filter"} data-testid="filter">*/}
                    {/*    /!*<img alt={"Filter"} src={TuneIcon} width={"30px"} />*!/*/}
                    {/*    <TuneIcon sx={{width:'30px',height:'30px'}} />*/}
                    {/*    <Typography fontSize={11} fontWeight={"bold"} color={"black"} fontFamily={"arial"}>Filter</Typography>*/}
                    {/*    <Typography color={"black"} visibility={"hidden"} marginTop={"-10px"}>_____</Typography>*/}
                    {/*</StyledButton>*/}

                </Box>

                {/*<Box display={"inline-flex"} style={{height:"fit-content", margin:"auto", marginLeft:"5px", marginRight:"5px"}} alignContent={"center"}>*/}


                {/*    <Box display={"inline-flex"} style={{border:"1px",borderStyle:"solid",borderColor:"black",borderRadius:"10px",height:"fit-content", margin:"auto", padding:"5px", marginLeft:"5px", marginRight:"auto"}} alignContent={"center"}>*/}
                {/*        <Typography fontSize={11} fontWeight={"bold"} color={"black"} fontFamily={"arial"} margin={"auto"}>Display Kids Services</Typography>*/}
                {/*        <Switch checked={kidstoggle || Category==="Kids"} onChange={handleKidsClick} />*/}
                {/*    </Box>*/}
                {/*</Box>*/}

                {/*      <Box sx={{display: "flex", marginRight: "auto", marginLeft: "auto", alignContent: "center"}}>
        <div className="slogan">
          <h4 id="slogan">One-stop shop for self-care</h4>

          <h4 data-testid="geoLocation">{geolocation[2]}</h4>

          {!keycloak.authenticated && (
              <button className="signupBtn" onClick={() => keycloak.login()}>
                Sign Up/Log In
              </button>
          )}
        </div>
      </Box>*/}


            </Box>
            {width <= Constants.MOBILE_WIDTH && !isProvider && (
                <Box display="flex" justifyContent="center" paddingTop="10px" paddingBottom="10px">
                    <Link
                        className="linkItem"
                        data-testid="mobile-list-business"
                        onClick={() => {
                            navigate("/registration", { replace: false});
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        List Your Business
                    </Link>
                </Box>
            )}
            <Search category={Category + (kidstoggle ? " Kids" : "")} distance={parsed.distance} latitude={parsed.latitude != null ? parsed.latitude : Latitude} longitude={parsed.longitude != null ? parsed.longitude : Longitude} accuracy={parsed.accuracy != null ? parsed.accuracy : Accuracy} city={(geoMode || (parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude)) ? "" : City} state={(geoMode || (parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude)) ? "" : State} page={Page} setPage={setPage} view={View} setView={setView} bounds={(parsed.minLatitude && parsed.maxLatitude && parsed.minLongitude && parsed.maxLongitude) ? { minLatitude: Number(parsed.minLatitude), maxLatitude: Number(parsed.maxLatitude), minLongitude: Number(parsed.minLongitude), maxLongitude: Number(parsed.maxLongitude) } : null}></Search>
        </Box>
    );
};

export default Home;